import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../../config/api-endpoints";
import { useServerPagination } from "../../../hooks/use-server-pagination";
import { useGet, usePost } from "../../../packages/hooks";
import { buildApiUrl } from "../../../packages/hooks/hook-utils";
import { useAuthStore } from "../../../store/useAuth";
import { Brand, Model, Product } from "../../../types";
import { getAuthHeaders } from "../../../utils/auth-utils";
import { UseProductsReturn } from "../types/products-types";
import {
  extractBrandsFromResponse,
  extractModelsFromResponse,
  extractProductsFromResponse,
} from "../utils/products-utils";
import {
  handleHttpError,
  extractErrorMessage,
} from "../../../utils/error-utils";

/**
 * Custom hook for product management using hooks from @hooks/ package
 * @param initialBrandId - Optional initial brand ID
 * @param selectedBranchId - Optional selected branch ID for filtering
 * @returns Product state and management functions
 */
export const useProducts = (
  initialBrandId?: string,
  selectedBranchId?: string | null
): UseProductsReturn => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Filter state
  const [selectedBrandId, setSelectedBrandId] = useState<string>(
    initialBrandId || ""
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");

  // Initialize server-side pagination
  const pagination = useServerPagination(totalItems);

  // Initialize hooks
  const productsQuery = useGet<{
    products: Product[];
    pagination: { total: number };
  }>(API_ENDPOINTS.PRODUCTS.LIST, {
    queryParams: {
      ...pagination.getQueryParams,
      ...(selectedBrandId && { brandId: selectedBrandId }),
      ...(selectedBranchId && { branchId: selectedBranchId }),
      ...(selectedStatus && { status: selectedStatus }),
      ...(searchTerm && { search: searchTerm }),
    },
    onSuccess: (data) => {
      // Try multiple possible structures
      let productList: Product[] = [];
      let totalCount = 0;

      if (data) {
        // Check for products array directly
        if (Array.isArray(data?.products)) {
          productList = data.products;
          totalCount = data?.pagination?.total || productList.length;
        }
        // Check for data.products structure
        else if (Array.isArray(data?.data?.products)) {
          productList = data.data.products;
          totalCount = data?.data?.pagination?.total || productList.length;
        }
        // Check if data itself is an array
        else if (Array.isArray(data)) {
          productList = data;
          totalCount = data.length;
        }
        // Check for data.data array structure
        else if (Array.isArray(data?.data)) {
          productList = data.data;
          totalCount = data.data.length;
        }
      }

      // Normalize products using utility function
      const normalizedProducts = extractProductsFromResponse(data);

      // Store all products
      setAllProducts(normalizedProducts);
      setTotalItems(totalCount);
    },
    onError: (error) => {
      toast.error("Ошибка при загрузке продуктов" + error);
    },
  });

  const brandsQuery = useGet<{
    brands: Brand[];
    pagination: { total: number };
  }>(API_ENDPOINTS.BRANDS.LIST, {
    queryParams: {
      page: 1,
      limit: 100, // Fetch maximum allowed brands for dropdown selection
      ...(selectedBranchId && { branchId: selectedBranchId }),
    },
  });

  const modelsQuery = useGet<{
    models: Model[];
    pagination: { total: number };
  }>(API_ENDPOINTS.MODELS.LIST, {
    queryParams: {
      page: 1,
      limit: 100, // Fetch maximum allowed models for dropdown selection
      ...(selectedBranchId && { branchId: selectedBranchId }),
    },
  });

  const models = useMemo(() => {
    return modelsQuery.data ? extractModelsFromResponse(modelsQuery.data) : [];
  }, [modelsQuery.data]);
  const brands = useMemo(() => {
    return brandsQuery.data ? extractBrandsFromResponse(brandsQuery.data) : [];
  }, [brandsQuery.data]);

  const createMutation = usePost<Product>(API_ENDPOINTS.PRODUCTS.LIST, {
    onSuccess: () => {
      toast.success("Продукт успешно создан");
      productsQuery.refetch();
    },
    onError: () => {
      toast.error("Ошибка при создании продукта");
    },
  });

  const fetchProducts = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await productsQuery.refetch();
    } catch (error) {
      toast.error("Ошибка при загрузке продуктов" + error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter products by licensePlate when searchTerm changes
  useEffect(() => {
    setProducts(allProducts);
  }, [allProducts]);

  // Note: No manual refetch needed here - useGet automatically refetches when queryParams change
  // The queryKey in useGet includes queryParams, so React Query handles this automatically

  const fetchBrands = useCallback(async (): Promise<void> => {
    try {
      await brandsQuery.refetch();
    } catch (error) {
      toast.error("Ошибка при загрузке брендов");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Don't include brandsQuery - it changes on every render

  const fetchModels = useCallback(async (): Promise<void> => {
    try {
      await modelsQuery.refetch();
    } catch (error) {
      toast.error("Ошибка при загрузке моделей");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Don't include modelsQuery - it changes on every render

  const handleCreateProduct = async (
    values: Partial<Product>
  ): Promise<any> => {
    try {
      setIsLoading(true);

      const payload = {
        ...values,
        ...(selectedBranchId && { branchId: selectedBranchId }),
      };

      // Log the payload being sent to API
      const response = await createMutation.mutateAsync(payload);

      return response;
    } catch (error) {
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProduct = async (
    id: string,
    values: Partial<Product>
  ): Promise<any> => {
    try {
      setIsLoading(true);

      // Construct endpoint with branchId query parameter
      let endpoint = API_ENDPOINTS.PRODUCTS.BY_ID(id);
      if (selectedBranchId) {
        endpoint += `?branchId=${selectedBranchId}`;
      }
      const url = buildApiUrl(endpoint);
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      // Prepare request body
      const requestBody: any = {
        id: id,
        ...values,
      };

      // Add branchId if available (ensure it's set properly)
      const branchId = selectedBranchId;
      if (branchId) {
        requestBody.branchId = branchId;
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(endpoint, token),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        await handleHttpError(response);
      }

      const result = await response.json();

      // Handle CRM API response format
      if (result.success && result.data) {
        productsQuery.refetch();
        toast.success("Продукт успешно обновлен");
        return response;
      }

      productsQuery.refetch();
      toast.success("Продукт успешно обновлен");
      return response;
    } catch (error) {
      const message =
        (error as any)?.message || "Ошибка при обновлении продукта";
      toast.error(message);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProduct = async (id: string): Promise<boolean> => {
    try {
      setIsLoading(true);

      // Construct endpoint with branchId query parameter
      let endpoint = API_ENDPOINTS.PRODUCTS.BY_ID(id);
      if (selectedBranchId) {
        endpoint += `?branchId=${selectedBranchId}`;
      }

      const url = buildApiUrl(endpoint);
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(endpoint, token),
        },
      });

      if (!response.ok) {
        await handleHttpError(response, {
          404: "Продукт не найден. Возможно, он уже удален или принадлежит другому филиалу.",
          409: "Конфликт данных. Возможно, продукт используется в других местах.",
        });
      }

      await productsQuery.refetch();
      toast.success("Продукт успешно удален");
      return true;
    } catch (error) {
      toast.error(extractErrorMessage(error, "Ошибка при удалении продукта"));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateProduct = async (product: Product): Promise<boolean> => {
    try {
      setIsLoading(true);

      // Create a duplicate product with modified name and without images
      const productDisplayName = `${product.brand || ""} ${
        product.model || ""
      }`.trim();

      // Create the name field (brand + model + year + lotNumber) with copied text
      const originalName = `${product.brand || ""} ${product.model || ""} ${
        product.year
      } ${product.lotNumber}`.trim();
      const duplicatedName = `${originalName} (скопирован)`;

      // Find brand and model IDs for the API
      const brandId = brands.find((b) => b.name === product.brand)?.id || "";

      // Find model that belongs to the specific brand
      const modelId =
        models.find((m) => m.name === product.model && m.brandId === brandId)
          ?.id || "";

      // Validate that we have valid IDs
      if (!brandId) {
        throw new Error(
          `Бренд "${product.brand}" не найден в списке доступных брендов`
        );
      }
      if (!modelId) {
        const availableModelsForBrand = models
          .filter((m) => m.brandId === brandId)
          .map((m) => m.name);
        throw new Error(
          `Модель "${product.model}" не найдена для бренда "${
            product.brand
          }". Доступные модели: ${availableModelsForBrand.join(", ")}`
        );
      }

      // Ensure we have a valid positive price (convert to number)
      // For legacy products from Firebase, use main price if rental price is 0
      const rentalPrice = Number(product.rental?.price) || 0;
      const mainPrice = Number(product.price) || 0;
      const productPrice =
        rentalPrice > 0 ? rentalPrice : mainPrice > 0 ? mainPrice : 1000;
      const validPrice = productPrice > 0 ? productPrice : 1000; // Ensure positive number

      // Ensure we have valid required fields (convert to numbers)
      const validMileage =
        Number(product.mileage) >= 0 ? Number(product.mileage) : 0;
      const validEngineSize =
        Number(product.engineSize) >= 0.1 ? Number(product.engineSize) : 1.0;
      const validYear =
        Number(product.year) >= 1900
          ? Number(product.year)
          : new Date().getFullYear();
      const validLicensePlate = product.licensePlate || "TEMP001";
      const validColor = product.color || "Белый";
      const validDescription = product.description || "Дублированный продукт";

      // Map Product fields to API expected format
      const duplicatedProduct: any = {
        name: duplicatedName, // API expects 'name' field with copied text
        brandId: brandId, // API expects brandId (database ID)
        modelId: modelId, // API expects modelId (database ID)
        mileage: validMileage,
        engineSize: validEngineSize,
        year: validYear,
        fuelType: product.fuelType || "GASOLINE",
        transmission: product.transmission || "MANUAL",
        drivetrain: product.drivetrain || "FWD",
        licensePlate: validLicensePlate,
        color: validColor,
        lotNumber: `${product.lotNumber}_copy`, // Add copied text to lot number
        rental: {
          status: product.rental?.status || "ACTIVE",
          rentalPeriod: product.rental?.rentalPeriod || "DAY",
          price: Number(validPrice), // Ensure price is a number
          hasDiscount: false, // Reset discount for duplicated product
          discountType: undefined,
          discountDuration: 0,
          discountAmount: 0, // Reset discount amount for duplicated product
          discountCreatedAt: null,
        },
        description: validDescription,
        order: Number(
          Math.max(...products.map((p) => Number(p.order) || 0), 0) + 1
        ), // Set to end of list
        isActive: false, // Set as inactive by default
        isFeatured: false, // Remove featured status
        meta: {
          title: `${productDisplayName} (скопирован)`,
          description: product.meta?.description || "Дублированный продукт",
          keywords: product.meta?.keywords || "дублированный, копия",
        },
        // Remove images as requested
        images: [],
        mainImage: "",
      };

      const success = await handleCreateProduct(duplicatedProduct);

      if (success) {
        toast.success("Продукт успешно дублирован");
      }

      return success;
    } catch (error) {
      const message =
        (error as any)?.message || "Ошибка при дублировании продукта";
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleReorderProduct = async (
    productId: string,
    newOrder: number
  ): Promise<boolean> => {
    // Store original state for rollback
    const originalProducts = [...products];

    try {
      setIsLoading(true);

      // Optimistic update: immediately update local state
      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === productId ? { ...product, order: newOrder } : product
        )
      );

      // Construct endpoint with branchId query parameter
      let endpoint = API_ENDPOINTS.PRODUCTS.BY_ID(productId);
      if (selectedBranchId) {
        endpoint += `?branchId=${selectedBranchId}`;
      }
      const url = buildApiUrl(endpoint);
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      // Prepare request body
      const requestBody: any = {
        id: productId,
        order: newOrder,
      };

      // Add branchId if available
      if (selectedBranchId) {
        requestBody.branchId = selectedBranchId;
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(endpoint, token),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        await handleHttpError(response);
      }

      const result = await response.json();

      // Handle CRM API response format
      if (result.success && result.data) {
        // Refresh data from server to ensure consistency
        productsQuery.refetch();
        toast.success("Порядок продукта успешно изменен");
        return true;
      }

      // Refresh data from server to ensure consistency
      productsQuery.refetch();
      toast.success("Порядок продукта успешно изменен");
      return true;
    } catch (error) {
      // Rollback optimistic update on error
      setProducts(originalProducts);

      toast.error(
        extractErrorMessage(error, "Ошибка при изменении порядка продукта")
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Filter handlers
  const handleBrandFilter = (brandId: string): void => {
    setSelectedBrandId(brandId);
    // Reset to first page when filtering
    pagination.handlePageChange(1);
  };

  const handleSearchFilter = (search: string): void => {
    setSearchTerm(search);
    // Reset to first page when searching
    pagination.handlePageChange(1);
  };

  const handleStatusFilter = (status: string): void => {
    setSelectedStatus(status);
    // Reset to first page when filtering
    pagination.handlePageChange(1);
  };

  const handleClearFilters = (): void => {
    setSelectedBrandId("");
    setSearchTerm("");
    setSelectedStatus("");
    // Reset to first page when clearing filters
    pagination.handlePageChange(1);
  };

  // Note: No initial fetch needed - useGet automatically fetches on mount and refetches when queryParams change

  return {
    products,
    brands,
    models,
    isLoading:
      isLoading ||
      productsQuery.isLoading ||
      productsQuery.isFetching ||
      brandsQuery.isLoading ||
      brandsQuery.isFetching ||
      modelsQuery.isLoading ||
      modelsQuery.isFetching ||
      createMutation.isPending,
    totalItems,
    fetchProducts,
    fetchBrands,
    fetchModels,
    handleCreateProduct,
    handleUpdateProduct,
    handleDeleteProduct,
    handleDuplicateProduct,
    handleReorderProduct,
    // Pagination handlers
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    totalPages: pagination.totalPages,
    handlePageChange: pagination.handlePageChange,
    handleItemsPerPageChange: pagination.handleItemsPerPageChange,
    // Filter handlers
    selectedBrandId,
    searchTerm,
    selectedStatus,
    handleBrandFilter,
    handleSearchFilter,
    handleStatusFilter,
    handleClearFilters,
  };
};
